import * as React from "react"
import { styled } from "styled-components"
import { Icon, IconTypes } from "src/global/ui/Icon"
import { Image } from "src/global/ui/Image"
import { images } from "src/global/ui/Image/images"
import { RadioGroup } from "src/global/ui/Radio/RadioGroup"
import { Radio, RadioChangeEvent } from "antd"

export type ExpectationType = "all" | "anomaly_detection" | "explicit"

export interface ExpectationSelectorProps {
  value?: ExpectationType
  onSelectionChange?: (value: ExpectationType) => void
}

type ImageType = keyof typeof images

interface IconOption {
  type: "icon"
  name: IconTypes
  props?: Partial<React.ComponentProps<typeof Icon>>
}

interface ImageOption {
  type: "image"
  name: ImageType
  props?: Partial<React.ComponentProps<typeof Image>>
}

type IconConfig = IconOption | ImageOption

interface ExpectationOption {
  value: ExpectationType
  label: string
  icon?: IconConfig
}

const expectationOptions: ExpectationOption[] = [
  {
    value: "all",
    label: "All",
  },
  {
    value: "anomaly_detection",
    label: "Anomaly Detection",
    icon: {
      type: "image",
      name: "sparkles",
    },
  },
  {
    value: "explicit",
    label: "Explicit",
    icon: {
      type: "icon",
      name: "sliders",
      props: { size: "16px" },
    },
  },
]

const StyledRadioGroup = styled(RadioGroup)`
  display: flex;
`

const RadioButton = styled(Radio.Button)`
  display: flex;
  align-items: center;
  height: auto;
`

const IconWrapper = styled.span`
  height: 16px;
  width: 16px;
  color: ${({ theme }) => theme.colors.neutralColorPalette.blacks.colorTextTertiary};

  .ant-radio-button-wrapper-checked & svg path {
    fill: white;
  }

  & > * {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const SparklesWrapper = styled.span`
  height: 16px;
  width: 16px;
  line-height: 0;

  svg {
    width: 16px;
    height: 16px;
  }

  svg path {
    fill: ${({ theme }) => theme.colors.neutralColorPalette.blacks.colorTextTertiary};
  }

  .ant-radio-button-wrapper-checked & svg path {
    fill: white;
  }
`

const LabelContent = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const ExpectationSelector: React.FC<ExpectationSelectorProps> = ({ value, onSelectionChange }) => {
  const currentValue = value || "all"
  const handleChange = (e: RadioChangeEvent) => {
    onSelectionChange?.(e.target.value as ExpectationType)
  }

  const IconElement = ({ iconConfig }: { iconConfig: IconConfig }) => {
    if (iconConfig.type === "icon") {
      return (
        <IconWrapper>
          <Icon name={iconConfig.name} {...iconConfig.props} />
        </IconWrapper>
      )
    }

    if (iconConfig.name === "sparkles") {
      return (
        <SparklesWrapper>
          <Image type={iconConfig.name} {...iconConfig.props} />
        </SparklesWrapper>
      )
    }

    return (
      <IconWrapper>
        <Image type={iconConfig.name} {...iconConfig.props} />
      </IconWrapper>
    )
  }

  return (
    <StyledRadioGroup value={currentValue} onChange={handleChange} buttonStyle="solid" optionType="button">
      {expectationOptions.map((option) => (
        <RadioButton key={option.value} value={option.value}>
          <LabelContent>
            {option.icon && <IconElement iconConfig={option.icon} />}
            {option.label}
          </LabelContent>
        </RadioButton>
      ))}
    </StyledRadioGroup>
  )
}
